import { useMemo } from 'react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { MessageSquare, Coins, Zap } from 'lucide-react'
import { useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { formatNumber, getResourceBadgeVariant } from '@/utils/number'

interface PlayerResourceLimitsProps {
  roomId: string
}

const COLOR_CLASSES = {
  sky: 'text-sky-600',
  purple: 'text-purple-600',
  green: 'text-green-600',
} as const

export default function PlayerResourceLimits({
  roomId: _roomId,
}: PlayerResourceLimitsProps) {
  const resourceLimits = useSelector(
    (state: RootState) => state.chat.resourceLimits
  )

  const resources = useMemo(() => {
    if (!resourceLimits?.resource_limits_enabled) return null

    return [
      {
        key: 'messages' as const,
        label: 'Messages',
        icon: MessageSquare,
        limit: resourceLimits.message_limit,
        used: resourceLimits.usage?.messages_used ?? 0,
        remaining: resourceLimits.remaining?.messages_remaining ?? null,
        color: 'sky' as const,
      },
      {
        key: 'tokens' as const,
        label: 'Tokens',
        icon: Zap,
        limit: resourceLimits.token_limit,
        used: resourceLimits.usage?.tokens_used ?? 0,
        remaining: resourceLimits.remaining?.tokens_remaining ?? null,
        color: 'purple' as const,
      },
      {
        key: 'currency' as const,
        label: 'Currency',
        icon: Coins,
        limit: resourceLimits.currency_limit,
        used: resourceLimits.usage?.currency_spent ?? 0,
        remaining: resourceLimits.remaining?.currency_remaining ?? null,
        color: 'green' as const,
      },
    ]
  }, [
    resourceLimits?.resource_limits_enabled,
    resourceLimits?.message_limit,
    resourceLimits?.token_limit,
    resourceLimits?.currency_limit,
    resourceLimits?.usage?.messages_used,
    resourceLimits?.usage?.tokens_used,
    resourceLimits?.usage?.currency_spent,
    resourceLimits?.remaining?.messages_remaining,
    resourceLimits?.remaining?.tokens_remaining,
    resourceLimits?.remaining?.currency_remaining,
  ])

  if (!resources) return null

  return (
    <Card className='p-4 bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200 shadow-sm'>
      <h3 className='text-sm font-semibold text-slate-700 mb-3'>
        Resource Limits
      </h3>

      <div className='space-y-3'>
        {resources.map((resource) => {
          const Icon = resource.icon
          const isUnlimited = resource.limit == null
          const limit = resource.limit ?? 0
          const remaining = resource.remaining ?? 0

          const isLow =
            !isUnlimited &&
            resource.remaining != null &&
            remaining <= limit * 0.2
          const isCritical =
            !isUnlimited &&
            resource.remaining != null &&
            remaining <= limit * 0.1

          const badgeVariant = getResourceBadgeVariant(isCritical, isLow)

          return (
            <div key={resource.key} className='space-y-1.5'>
              <div className='flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <Icon
                    className={cn('h-4 w-4', COLOR_CLASSES[resource.color])}
                  />
                  <span className='text-xs font-medium text-slate-600'>
                    {resource.label}
                  </span>
                </div>
                <div className='flex items-center gap-2'>
                  {!isUnlimited && (
                    <span className='text-xs font-semibold text-slate-700'>
                      {formatNumber(resource.used)} / {formatNumber(limit)}
                    </span>
                  )}
                  {!isUnlimited && resource.remaining != null && (
                    <Badge
                      variant={badgeVariant}
                      className='text-xs h-5 px-1.5'
                    >
                      {formatNumber(resource.remaining)} left
                    </Badge>
                  )}
                  {isUnlimited && (
                    <Badge
                      variant='outline'
                      className='text-xs h-5 px-1.5 text-slate-500'
                    >
                      Unlimited
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </Card>
  )
}
